import '@scripts/wdyr';
import React from 'react';
import App, { AppProps } from 'next/app';
import AppLayout from '@components/Utils/AppLayout';
import { AuthProvider } from '@providers/AuthProvider';
import { Provider as BusProvider } from 'react-bus';
import { globals } from '@styles/globals';
import { Global, ThemeProvider } from '@emotion/react';
import { withRouter } from 'next/router';
import { RouterContextProvider } from '@hooks/useRouter';
import PageTransition from '@components/Animation/PageTransition';
import { I18NextReactProvider } from '@i18n/I18NextReactProvider';
import { LangCode } from '@i18n/languages';
import { createI18nInstance } from '@i18n/i18next-factory';
import Head from 'next/head';
import { SearchProvider } from '@providers/SearchProvider';
import attenderTheme from '@styles/themes';
import styled from '@emotion/styled';
import Bugsnag from '@utils/bugsnag';
import { useStore } from '@store/appStore';
import { isOutdated, reloadForNewVersion } from '@utils/versionCheck';
const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

const Wrapper = styled.div<{ darkMode: boolean }>`
  background: ${(props) => (props.darkMode ? '#1f1f1f' : props.theme.colors.offWhite)};
`;

// LogRocket.init('x1twmd/attender');
interface ISSRComponent {
  Component: any;
  pageProps: any;
}

class AttenderApp extends App<AppProps> {
  constructor(props: AppProps) {
    super(props);
  }

  // The SW's own skipWaiting+clientsClaim config (next.config.js) makes a
  // new service worker take over immediately once it's downloaded, but that
  // alone doesn't refresh an already-open tab's already-loaded JS chunks -
  // it just means the NEXT fetch goes through the new worker. Without this,
  // a tab left open across a release can end up asking the new worker for a
  // chunk hash that no longer matches what it has cached/precached, which is
  // what surfaced as "not executable" console errors until a hard refresh
  // (ctrl+F5) bypassed the worker entirely. Reloading once the new worker
  // actually takes control closes that gap. `isUpdate` guards against
  // reloading on a plain first-time install (no previous controller yet).
  // Only when this page really is behind the live build, though: the new
  // worker also takes control right after a page has just loaded the new build
  // (e.g. the version check's own reload), and reloading that one again was
  // pointless - the same check and loop guard as useVersionCheck decide.
  componentDidMount() {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      const wb = (window as any).workbox;
      if (wb) {
        wb.addEventListener('controlling', async (event: { isUpdate?: boolean }) => {
          if (!event.isUpdate) return;
          if (await isOutdated()) {
            if (!reloadForNewVersion()) useStore.getState().setNewVersionAvailable(true);
          }
        });

        // next-pwa's own auto-register is off (next.config.js): some browsers refuse service
        // workers outright - extensions or locked-down/embedded browsers wrapping
        // navigator.serviceWorker.register (seen in Bugsnag as "unknown wrsParams..."). The app
        // works fine without one (just no offline caching), so that's a breadcrumb, not an error.
        wb.register().catch((error: unknown) => {
          Bugsnag.leaveBreadcrumb('Service worker registration refused', {
            reason: String((error as any)?.message ?? error),
          });
        });
      }
    }
  }

  render() {
    const { Component: SsrComponent, pageProps: ssrPageProps }: ISSRComponent = this.props;

    return (
      <ErrorBoundary>
        <ThemeProvider theme={attenderTheme}>
          <Wrapper darkMode={this.props.router.query.theme === 'dark'} className='notranslate'>
            <div id="modalRoot"></div>
            <I18NextReactProvider factory={createI18nInstance} lang={LangCode.en}>
              <AuthProvider>
                <SearchProvider>
                  <Head>
                    <meta name="viewport" content="width=device-width, user-scalable=no"></meta>
                  </Head>
                  <BusProvider>
                    <RouterContextProvider>
                      <AppLayout>
                        <PageTransition>
                          {({ Component, pageProps }: ISSRComponent) => {
                            return Component ? <Component {...pageProps} /> : <SsrComponent {...ssrPageProps} />;
                          }}
                        </PageTransition>
                      </AppLayout>
                      <Global styles={globals} />
                    </RouterContextProvider>
                  </BusProvider>
                </SearchProvider>
              </AuthProvider>
            </I18NextReactProvider>
          </Wrapper>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }
}

// required to read/set cookie in our middleware
export default withRouter(AttenderApp);
